import {i18n} from "../../i18n.ts"
import {
  AKlaraDashboardActivityTypeEnum,
  AKlaraDashboardDeal,
  AKlaraDashboardDealChipValues,
  AKlaraDashboardLifecycleStatusValues,
} from "../../services/types.generated.ts"
import {apiDateToJS} from "../../utils/dateArithmetics.ts"
import {formatDate} from "./components/formatDate.ts"
import {wallClock} from "./sessions.ts"
import {TDashboardClock} from "./useDashboardClock.ts"

/** A deal's label; the same vocabulary as the leads table's status chips. */
export type TKlaraDisplayStatus = AKlaraDashboardDealChipValues

type TStatusDeal = Pick<
  AKlaraDashboardDeal,
  "lifecycle_status" | "flags" | "overdue_activity" | "stage_days" | "closed_at"
>

type TIssue =
  AKlaraDashboardDealChipValues.Missing | AKlaraDashboardDealChipValues.Overdue | AKlaraDashboardDealChipValues.Rotten

// The label precedence of an open deal's flags; it never decides which deals a filter or count includes.
const ISSUE_PRECEDENCE: TIssue[] = [
  AKlaraDashboardDealChipValues.Missing,
  AKlaraDashboardDealChipValues.Overdue,
  AKlaraDashboardDealChipValues.Rotten,
]

/** The one label a deal shows: Won / Lost when closed, else Missing → Overdue → Rotten → Planned. */
export const displayStatus = (deal: Pick<TStatusDeal, "lifecycle_status" | "flags">): TKlaraDisplayStatus => {
  switch (deal.lifecycle_status) {
    case AKlaraDashboardLifecycleStatusValues.Won:
      return AKlaraDashboardDealChipValues.Won
    case AKlaraDashboardLifecycleStatusValues.Lost:
      return AKlaraDashboardDealChipValues.Lost
    case AKlaraDashboardLifecycleStatusValues.Open:
      return ISSUE_PRECEDENCE.find(issue => deal.flags[issue]) ?? AKlaraDashboardDealChipValues.Planned
  }
}

export const statusLabel = (status: TKlaraDisplayStatus): string => {
  switch (status) {
    case AKlaraDashboardDealChipValues.Missing:
      return i18n.t("KlaraDashboard_Leads_Status_Missing")
    case AKlaraDashboardDealChipValues.Overdue:
      return i18n.t("KlaraDashboard_Leads_Status_Overdue")
    case AKlaraDashboardDealChipValues.Rotten:
      return i18n.t("KlaraDashboard_Leads_Status_Rotten")
    case AKlaraDashboardDealChipValues.Planned:
      return i18n.t("KlaraDashboard_Leads_Status_Planned")
    case AKlaraDashboardDealChipValues.Won:
      return i18n.t("KlaraDashboard_Leads_Status_Won")
    case AKlaraDashboardDealChipValues.Lost:
      return i18n.t("KlaraDashboard_Leads_Status_Lost")
  }
}

export const activityTypeLabel = (type: AKlaraDashboardActivityTypeEnum): string => {
  switch (type) {
    case AKlaraDashboardActivityTypeEnum.Call:
      return i18n.t("KlaraDashboard_Leads_TaskType_Call")
    case AKlaraDashboardActivityTypeEnum.Email:
      return i18n.t("KlaraDashboard_Leads_TaskType_Email")
    case AKlaraDashboardActivityTypeEnum.Meeting:
      return i18n.t("KlaraDashboard_Leads_TaskType_Meeting")
    case AKlaraDashboardActivityTypeEnum.Other:
      return i18n.t("KlaraDashboard_Leads_TaskType_Other")
  }
}

/** "25 Sep" for a calendar day of the dashboard's current year, "25 Sep 2025" otherwise. */
export const formatDealDay = (date: Date, clock: TDashboardClock): string => {
  const sameYear = date.getFullYear() === wallClock(clock.asOf, clock.timeZone).year
  // Noon keeps the formatted day clear of the browser's own DST transitions.
  const noon = new Date(date.getFullYear(), date.getMonth(), date.getDate(), 12)

  return formatDate(noon.getTime(), sameYear ? "d MMM" : "d MMM yyyy")
}

/** A `yyyy-MM-dd` activity date; these are calendar days, never shifted by a time zone. */
export const formatActivityDate = (date: string, clock: TDashboardClock): string =>
  formatDealDay(apiDateToJS(date), clock)

const issueLine = (deal: TStatusDeal, issue: TIssue, clock: TDashboardClock): string | null => {
  switch (issue) {
    case AKlaraDashboardDealChipValues.Missing:
      return i18n.t("KlaraDashboard_Leads_Issue_NothingPlanned")
    case AKlaraDashboardDealChipValues.Overdue:
      return deal.overdue_activity
        ? i18n.t("KlaraDashboard_Leads_Issue_WasDue", {
            type: activityTypeLabel(deal.overdue_activity.type),
            date: formatActivityDate(deal.overdue_activity.date, clock),
          })
        : i18n.t("KlaraDashboard_Leads_Issue_Overdue")
    case AKlaraDashboardDealChipValues.Rotten:
      return deal.stage_days == null ? null : i18n.t("KlaraDashboard_Leads_Issue_SameStage", {count: deal.stage_days})
  }
}

/** Every issue of an open deal (in label precedence), e.g. "Call was due 25 Sep"; none for a closed deal. */
export const issueLines = (deal: TStatusDeal, clock: TDashboardClock): string[] => {
  if (deal.lifecycle_status !== AKlaraDashboardLifecycleStatusValues.Open) {
    return []
  }

  return ISSUE_PRECEDENCE.filter(issue => deal.flags[issue])
    .map(issue => issueLine(deal, issue, clock))
    .filter(line => line !== null)
}

/** The line under the status label: the labelled issue, the close day of a closed deal, nothing when planned. */
export const statusSubline = (deal: TStatusDeal, clock: TDashboardClock): string | null => {
  const status = displayStatus(deal)

  switch (status) {
    case AKlaraDashboardDealChipValues.Missing:
    case AKlaraDashboardDealChipValues.Overdue:
    case AKlaraDashboardDealChipValues.Rotten:
      return issueLine(deal, status, clock)
    case AKlaraDashboardDealChipValues.Planned:
      return null
    case AKlaraDashboardDealChipValues.Won:
    case AKlaraDashboardDealChipValues.Lost: {
      if (!deal.closed_at) {
        return null
      }
      const closed = wallClock(new Date(deal.closed_at), clock.timeZone)
      return i18n.t("KlaraDashboard_Leads_Issue_Closed", {
        date: formatDealDay(new Date(closed.year, closed.month - 1, closed.day), clock),
      })
    }
  }
}
